import { rerollBadge } from '@fp/badges';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '../components/badges';
import { Topbar } from '../components/Shell';
import { Confirm, Empty, ErrorBox, Field, Loading, Modal, useAction } from '../components/ui';
import { del, get, patch, post, qs } from '../lib/api';
import { fmtAgo, fmtDate, fmtDateTime, fromLocalInput } from '../lib/format';
import type { BadgeSpec, LeagueRow, UserRow } from '../lib/types';

// ================================================================ users

export function UsersPage() {
  const search = useSearch({ strict: false }) as { q?: string; role?: string; banned?: string; page?: number };
  const navigate = useNavigate();
  const page = search.page ?? 0;
  const [text, setText] = useState(search.q ?? '');
  const q = useQuery({
    queryKey: ['users', search],
    queryFn: () => get<{ rows: UserRow[]; total: number }>(`/admin/users${qs({ q: search.q, role: search.role, banned: search.banned, limit: 50, offset: page * 50 })}`),
  });
  const set = (p: Record<string, unknown>) => navigate({ to: '/users', search: { ...search, page: undefined, ...p } });
  return (
    <>
      <Topbar title="Users" sub={q.data ? `${q.data.total.toLocaleString()} accounts` : undefined} />
      <div className="page">
        <div className="card">
          <form
            className="card-head"
            onSubmit={(e) => {
              e.preventDefault();
              set({ q: text || undefined });
            }}
          >
            <input className="input" placeholder="Email, username or name" aria-label="Search users" value={text} onChange={(e) => setText(e.target.value)} style={{ width: 260 }} />
            <select className="select" aria-label="Role" value={search.role ?? ''} onChange={(e) => set({ role: e.target.value || undefined })}>
              <option value="">Any role</option>
              <option value="admin">Admins</option>
              <option value="user">Players</option>
            </select>
            <select className="select" aria-label="Ban" value={search.banned ?? ''} onChange={(e) => set({ banned: e.target.value || undefined })}>
              <option value="">Banned or not</option>
              <option value="true">Banned</option>
              <option value="false">Not banned</option>
            </select>
            <button className="btn">Search</button>
          </form>
          {q.error && <ErrorBox error={q.error} />}
          {q.isLoading ? (
            <Loading />
          ) : q.data?.rows.length === 0 ? (
            <Empty>No accounts match.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Country</th>
                    <th>Joined</th>
                    <th className="right">Predictions</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.data?.rows.map((u) => (
                    <tr key={u.id} className="clickable" onClick={() => navigate({ to: '/users/$userId', params: { userId: u.id } })}>
                      <td>
                        <strong>{u.displayUsername ?? u.username ?? u.name}</strong>
                      </td>
                      <td>{u.email}</td>
                      <td>{u.country ?? '—'}</td>
                      <td>{fmtDate(u.createdAt)}</td>
                      <td className="right num">{u.predictions}</td>
                      <td className="right">
                        <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                          {u.role === 'admin' && <span className="pill pill-dark">Admin</span>}
                          {u.banned && <span className="pill pill-red">Banned</span>}
                          {!u.emailVerified && <span className="pill">Unverified</span>}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {q.data && q.data.total > 50 && (
            <div className="card-foot row">
              <span className="grow">
                {page * 50 + 1}–{Math.min((page + 1) * 50, q.data.total)} of {q.data.total}
              </span>
              <button type="button" className="btn btn-sm" disabled={page === 0} onClick={() => navigate({ to: '/users', search: { ...search, page: page - 1 } })}>
                Previous
              </button>
              <button type="button" className="btn btn-sm" disabled={(page + 1) * 50 >= q.data.total} onClick={() => navigate({ to: '/users', search: { ...search, page: page + 1 } })}>
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

interface UserDetail {
  user: UserRow & { role: string | null; banned: boolean | null; birthMonth: string | null; updatedAt: string };
  stats: { predictions: number; exact: number; points: number };
  seasonPoints: number;
  leagues: { id: number; name: string; role: string; members: number; joinedAt: string }[];
  sessions: { id: string; createdAt: string; expiresAt: string; ipAddress: string | null; userAgent: string | null }[];
}

export function UserDetailPage() {
  const { userId } = useParams({ strict: false }) as { userId: string };
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['user', userId], queryFn: () => get<UserDetail>(`/admin/users/${encodeURIComponent(userId)}`) });
  const { busy, run } = useAction();
  const [modal, setModal] = useState<null | 'ban' | 'rename' | 'delete'>(null);
  if (q.isLoading) return <Loading />;
  if (q.error) return <div className="page"><ErrorBox error={q.error} /></div>;
  const d = q.data!;
  const u = d.user;
  const base = `/admin/users/${encodeURIComponent(userId)}`;
  const act = (fn: () => Promise<unknown>, ok: string) =>
    run(async () => {
      await fn();
      await qc.invalidateQueries({ queryKey: ['user', userId] });
      await qc.invalidateQueries({ queryKey: ['users'] });
    }, ok);
  return (
    <>
      <Topbar
        title={u.displayUsername ?? u.username ?? u.name}
        sub={`${u.email} · joined ${fmtDate(u.createdAt)}`}
        actions={
          <>
            {u.role === 'admin' && <span className="pill pill-dark">Admin</span>}
            {u.banned && <span className="pill pill-red">Banned</span>}
          </>
        }
      />
      <div className="page">
        <div className="cols cols-main">
          <div className="stack" style={{ gap: 20 }}>
            <section className="card">
              <div className="card-head">
                <h2>Account</h2>
              </div>
              <div className="card-body">
                <dl className="kv">
                  <dt>Name</dt>
                  <dd>{u.name}</dd>
                  <dt>Email</dt>
                  <dd>
                    {u.email} {u.emailVerified ? <span className="pill pill-green">Verified</span> : <span className="pill">Not verified: hidden from public boards</span>}
                  </dd>
                  <dt>Country</dt>
                  <dd>{u.country ?? '—'}</dd>
                  <dt>Born</dt>
                  <dd>{u.birthMonth ?? '—'}</dd>
                  <dt>Ban</dt>
                  <dd>{u.banned ? `${u.banReason ?? 'No reason given'}${u.banExpires ? ` · until ${fmtDateTime(u.banExpires)}` : ' · permanent'}` : 'Not banned'}</dd>
                  <dt>Predictions</dt>
                  <dd className="num">
                    {d.stats.predictions} ({d.stats.exact} exact) · {d.seasonPoints} season points
                  </dd>
                </dl>
              </div>
            </section>
            <section className="card">
              <div className="card-head">
                <h2>Leagues</h2>
              </div>
              {d.leagues.length === 0 ? (
                <Empty>Not in any private league.</Empty>
              ) : (
                <table className="table">
                  <tbody>
                    {d.leagues.map((l) => (
                      <tr key={l.id} className="clickable" onClick={() => navigate({ to: '/leagues/$leagueId', params: { leagueId: String(l.id) } })}>
                        <td>
                          <strong>{l.name}</strong>
                        </td>
                        <td>{l.role.toLowerCase()}</td>
                        <td className="right num">{l.members} members</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
            <section className="card">
              <div className="card-head">
                <h2>Signed-in devices</h2>
              </div>
              {d.sessions.length === 0 ? (
                <Empty>No active sessions.</Empty>
              ) : (
                <table className="table">
                  <tbody>
                    {d.sessions.map((s) => (
                      <tr key={s.id}>
                        <td className="small">{s.userAgent ?? 'Unknown device'}</td>
                        <td className="muted small">{s.ipAddress ?? ''}</td>
                        <td className="right muted small">{fmtAgo(s.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
          <section className="card">
            <div className="card-head">
              <h2>Actions</h2>
            </div>
            <div className="card-body stack">
              <button type="button" className="btn" disabled={busy} onClick={() => act(() => post(`${base}/role`, { role: u.role === 'admin' ? 'user' : 'admin' }), u.role === 'admin' ? 'Admin role removed' : 'Made an admin')}>
                {u.role === 'admin' ? 'Remove admin role' : 'Make admin'}
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => act(() => post(`${base}/verify`, { verified: !u.emailVerified }), u.emailVerified ? 'Marked unverified' : 'Marked verified')}>
                {u.emailVerified ? 'Mark email unverified' : 'Mark email verified'}
              </button>
              <button type="button" className="btn" onClick={() => setModal('rename')}>
                Change username or name
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => act(() => post(`${base}/sign-out`), 'Signed out everywhere')}>
                Sign out everywhere
              </button>
              {u.banned ? (
                <button type="button" className="btn" disabled={busy} onClick={() => act(() => post(`${base}/unban`), 'Ban lifted')}>
                  Lift ban
                </button>
              ) : (
                <button type="button" className="btn btn-danger" onClick={() => setModal('ban')}>
                  Ban
                </button>
              )}
              <button type="button" className="btn btn-danger" onClick={() => setModal('delete')}>
                Delete account
              </button>
            </div>
          </section>
        </div>
      </div>
      {modal === 'ban' && <BanForm onClose={() => setModal(null)} onBan={(reason, until) => act(() => post(`${base}/ban`, { reason, expiresAt: until }), 'Player banned')} />}
      {modal === 'rename' && (
        <RenameForm
          username={u.displayUsername ?? u.username ?? ''}
          name={u.name}
          onClose={() => setModal(null)}
          onSave={(body) => act(() => patch(base, body), 'Saved')}
        />
      )}
      {modal === 'delete' && (
        <Confirm
          title="Delete this account?"
          body="Their predictions, memberships and points are erased. Leagues they own pass to their longest-standing member. This can't be undone."
          confirmLabel="Delete account"
          danger
          onClose={() => setModal(null)}
          onConfirm={async () => {
            await del(base);
            await qc.invalidateQueries({ queryKey: ['users'] });
            navigate({ to: '/users' });
          }}
        />
      )}
    </>
  );
}

function BanForm({ onClose, onBan }: { onClose: () => void; onBan: (reason: string, until: string | null) => Promise<unknown> }) {
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');
  return (
    <Modal
      title="Ban player"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={!reason.trim()}
            onClick={async () => {
              await onBan(reason.trim(), until ? fromLocalInput(until) : null);
              onClose();
            }}
          >
            Ban
          </button>
        </>
      }
    >
      <p className="muted" style={{ margin: 0 }}>
        They're signed out at once, can't sign in, and disappear from public boards. Their league members still see them in league tables.
      </p>
      <Field label="Reason (kept in the audit log)">
        <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Field label="Until" hint="Leave empty for a permanent ban">
        <input className="input" type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
      </Field>
    </Modal>
  );
}

function RenameForm({ username, name, onClose, onSave }: { username: string; name: string; onClose: () => void; onSave: (b: { username?: string; name?: string }) => Promise<unknown> }) {
  const [u, setU] = useState(username);
  const [n, setN] = useState(name);
  return (
    <Modal
      title="Change username or name"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={async () => {
              await onSave({ ...(u !== username ? { username: u } : {}), ...(n !== name ? { name: n } : {}) });
              onClose();
            }}
          >
            Save
          </button>
        </>
      }
    >
      <Field label="Username" hint="Shown on public boards. 3 to 30 letters, digits, dots or underscores.">
        <input className="input" value={u} onChange={(e) => setU(e.target.value)} />
      </Field>
      <Field label="Name">
        <input className="input" value={n} onChange={(e) => setN(e.target.value)} />
      </Field>
    </Modal>
  );
}

// ================================================================ private leagues

export function LeaguesPage() {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['leagues', q], queryFn: () => get<{ rows: LeagueRow[]; total: number }>(`/admin/leagues${qs({ q, limit: 100 })}`) });
  return (
    <>
      <Topbar title="Private leagues" sub={list.data ? `${list.data.total} leagues` : undefined}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setQ(text);
          }}
        >
          <input className="input" placeholder="Name, invite code or owner email" aria-label="Search leagues" value={text} onChange={(e) => setText(e.target.value)} style={{ width: 280 }} />
        </form>
      </Topbar>
      <div className="page">
        <div className="card">
          {list.error && <ErrorBox error={list.error} />}
          {list.isLoading ? (
            <Loading />
          ) : list.data?.rows.length === 0 ? (
            <Empty>No leagues found.</Empty>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>League</th>
                  <th>Owner</th>
                  <th>Counts</th>
                  <th>Code</th>
                  <th className="right">Members</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.rows.map((l) => (
                  <tr key={l.id} className="clickable" onClick={() => navigate({ to: '/leagues/$leagueId', params: { leagueId: String(l.id) } })}>
                    <td>
                      <span className="row" style={{ flexWrap: 'nowrap' }}>
                        <Badge spec={l.badge} size={26} />
                        <strong>{l.name}</strong>
                      </span>
                    </td>
                    <td>{l.ownerName ?? '—'}</td>
                    <td>{l.competitionName ?? 'All matches'}</td>
                    <td className="num">{l.inviteCode}</td>
                    <td className="right num">
                      {l.members} / {l.maxMembers}
                    </td>
                    <td>{fmtDate(l.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

interface LeagueDetail {
  league: { id: number; name: string; badge: BadgeSpec; inviteCode: string | null; startsAt: string; maxMembers: number; ownerId: string | null; competitionName: string | null; members: number; createdAt: string };
  table: { rank: number; userId: string; name: string; points: number; exactCount: number; predictionsCount: number }[];
  members: { userId: string; role: string; joinedAt: string; email: string; name: string }[];
}

export function LeagueDetailPage() {
  const { leagueId } = useParams({ strict: false }) as { leagueId: string };
  const id = Number(leagueId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['league', id], queryFn: () => get<LeagueDetail>(`/admin/leagues/${id}`) });
  const { busy, run } = useAction();
  const [modal, setModal] = useState<null | 'rename' | 'delete' | { remove: string }>(null);
  const [max, setMax] = useState<string>('');
  if (q.isLoading) return <Loading />;
  if (q.error) return <div className="page"><ErrorBox error={q.error} /></div>;
  const { league: l, table, members } = q.data!;
  const act = (fn: () => Promise<unknown>, ok: string) =>
    run(async () => {
      await fn();
      await qc.invalidateQueries({ queryKey: ['league', id] });
      await qc.invalidateQueries({ queryKey: ['leagues'] });
    }, ok);
  return (
    <>
      <Topbar
        title={
          <span className="row">
            <Badge spec={l.badge} size={40} /> {l.name}
          </span>
        }
        sub={`${l.members} of ${l.maxMembers} members · ${l.competitionName ?? 'all matches'} · counting from ${fmtDate(l.startsAt)} · code ${l.inviteCode}`}
        actions={
          <>
            <button type="button" className="btn" onClick={() => setModal('rename')}>
              Rename
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => act(() => post(`/admin/leagues/${id}/invite`), 'New invite code made')}>
              New invite code
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => act(() => patch(`/admin/leagues/${id}`, { badge: rerollBadge(l.badge, Date.now()) }), 'Badge shuffled')}>
              Shuffle badge
            </button>
            <button type="button" className="btn btn-danger" onClick={() => setModal('delete')}>
              Delete league
            </button>
          </>
        }
      />
      <div className="page">
        <div className="cols cols-2">
          <section className="card">
            <div className="card-head">
              <h2>Members</h2>
              <form
                className="actions"
                onSubmit={(e) => {
                  e.preventDefault();
                  void act(() => patch(`/admin/leagues/${id}`, { maxMembers: Number(max) }), 'Member limit saved');
                }}
              >
                <input className="input" type="number" min={2} max={5000} aria-label="Member limit" placeholder={`Limit ${l.maxMembers}`} value={max} onChange={(e) => setMax(e.target.value)} style={{ width: 120 }} />
                <button className="btn btn-sm" disabled={!max || busy}>
                  Set limit
                </button>
              </form>
            </div>
            <table className="table">
              <tbody>
                {members.map((m) => (
                  <tr key={m.userId}>
                    <td>
                      <strong>{m.name}</strong>
                      <div className="muted small">{m.email}</div>
                    </td>
                    <td>{m.role === 'OWNER' ? <span className="pill pill-dark">Owner</span> : m.role.toLowerCase()}</td>
                    <td className="muted small">joined {fmtDate(m.joinedAt)}</td>
                    <td className="right">
                      <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {m.role !== 'OWNER' && (
                          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => act(() => post(`/admin/leagues/${id}/owner`, { userId: m.userId }), `${m.name} now owns the league`)}>
                            Make owner
                          </button>
                        )}
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setModal({ remove: m.userId })}>
                          Remove
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <section className="card">
            <div className="card-head">
              <h2>Table</h2>
            </div>
            <table className="table">
              <tbody>
                {table.map((r) => (
                  <tr key={r.userId}>
                    <td className="display num" style={{ width: 36, fontSize: 18 }}>
                      {r.rank}
                    </td>
                    <td>{r.name}</td>
                    <td className="right display num" style={{ fontSize: 18 }}>
                      {r.points}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      </div>
      {modal === 'rename' && (
        <RenameLeague name={l.name} onClose={() => setModal(null)} onSave={(name) => act(() => patch(`/admin/leagues/${id}`, { name }), 'League renamed')} />
      )}
      {modal === 'delete' && (
        <Confirm
          title={`Delete ${l.name}?`}
          body="The league and its memberships are deleted. Players keep their predictions and points."
          confirmLabel="Delete league"
          danger
          onClose={() => setModal(null)}
          onConfirm={async () => {
            await del(`/admin/leagues/${id}`);
            await qc.invalidateQueries({ queryKey: ['leagues'] });
            navigate({ to: '/leagues' });
          }}
        />
      )}
      {modal && typeof modal === 'object' && (
        <Confirm
          title="Remove this member?"
          body="They leave the league. If they own it, it passes to the longest-standing member."
          confirmLabel="Remove"
          danger
          onClose={() => setModal(null)}
          onConfirm={() => act(() => del(`/admin/leagues/${id}/members/${encodeURIComponent(modal.remove)}`), 'Member removed')}
        />
      )}
    </>
  );
}

function RenameLeague({ name, onClose, onSave }: { name: string; onClose: () => void; onSave: (n: string) => Promise<unknown> }) {
  const [n, setN] = useState(name);
  return (
    <Modal
      title="Rename league"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={async () => {
              await onSave(n);
              onClose();
            }}
          >
            Save
          </button>
        </>
      }
    >
      <Field label="Name">
        <input className="input" value={n} maxLength={40} onChange={(e) => setN(e.target.value)} />
      </Field>
    </Modal>
  );
}
